import { sendErrorBeacon } from './client'
import { CLIENT_BUILD } from '../pwa/build'

/**
 * The ONE function in this package that decides WHAT a client error report
 * contains and WHETHER it is worth sending (review §5.5's decision,
 * `packages/api/src/worker.ts`'s own doc comment on `POST /api/client-error`
 * for the full rationale — Workers Logs via `console.error`, not Sentry).
 * `ErrorBoundary.tsx` and `installGlobalErrorReporting` below both call this
 * and construct nothing of their own — repointing the sink at Sentry later
 * (or anywhere else) is a change to this file and `api/client.ts`'s
 * `sendErrorBeacon`, no call site anywhere else in `packages/web` needs to
 * move.
 *
 * WHERE the report goes moved to `sendErrorBeacon` (`api/client.ts`) at
 * web-pre-deploy item 11 — the URL, the `keepalive: true` that gives a
 * report a chance to survive the navigation a crash often triggers, and the
 * swallowed failure all live there now, with their reasoning. Everything
 * below — the two limits, the four `.slice` caps, the dedupe key's shape —
 * is this reporter's own policy and stayed here: it is about what is worth
 * reporting, not about how a POST is made.
 *
 * Failures are swallowed by the beacon: an error reporter that itself throws
 * while reporting an error creates a loop, or at best a second error with
 * nowhere better to go than back into this same function. Silently losing an
 * occasional report (offline, the API itself down) is the accepted cost —
 * there is no secondary sink to fall back to, by design (see the route's own
 * doc comment on why a second vendor was rejected).
 *
 * NO USER CONTENT crosses this boundary. `error.message`/`error.stack` are
 * whatever the THROWING CODE chose to put in an `Error` — never a form
 * value, a fold row, or anything this function reads out of application
 * state itself. EVERY string field is capped client-side too (`.slice`,
 * matching `clientErrorBody`'s own server-side caps in `worker.ts` exactly:
 * `clientBuild` 64, `route` 256, `message` 2 000, `stack` 8 000) — the
 * server would reject an oversized field with a 400 anyway, but truncating
 * here means a huge value still gets reported (truncated), not dropped
 * entirely by a 400 round trip that itself teaches this function nothing
 * new to do. `kind` needs no cap: it is a closed three-value enum, not a
 * string this function ever grows.
 *
 * CORRECTED, review round 2's Important 3: an earlier version of this
 * comment made the same "every field is capped" claim while the code below
 * only sliced `message`/`stack` — `clientBuild` and `route` passed through
 * raw. `route` in particular is DERIVED FROM RUNTIME STATE
 * (`window.location.pathname`, not a literal this file controls), so an
 * unusually long pathname would have 400'd at the server, and
 * `.catch(() => {})` above would have swallowed that 400 SILENTLY — the
 * exact "chunk failure goes silent" failure class this whole task exists to
 * close, just for a different field. Fixed by slicing all four.
 *
 * FINAL REVIEW, bucket 1 item 6 — NO CAP, and the flood was invisible:
 * before this fix, this function POSTed once per `window` `error`/
 * `unhandledrejection` and once per `ErrorBoundary` catch, FOREVER, with
 * `.catch(() => {})` above hiding every failure from the console too. Task
 * 17 already hit the live version of this (an IndexedDB fault producing a
 * report per tab focus) and fixed it at ONE call site (`void
 * drain().catch(() => undefined)`, `outbox/drain.ts`); this reporter itself
 * — the thing every OTHER unbounded-throw source still funnels through —
 * was unchanged. A throw inside a `setInterval`, or React's own
 * render-retry on a crashing component, produces an unbounded stream with
 * nothing here to stop it.
 *
 * TWO independent limits, checked in this order:
 *  1. DEDUPE on `kind + message` — the realistic repeat case (the same
 *     background timer or the same crashing component throwing the
 *     IDENTICAL error again and again) is dropped before it ever touches
 *     the cap below, so one genuinely NEW error later in the session is
 *     not silently lost to a budget a single repeating fault already spent.
 *  2. A CAP on distinct reports actually sent (20 — generous: a real crash
 *     loop is usually one or two distinct causes, not twenty). Checked
 *     only for a message that clears the dedupe check above, so the two
 *     limits compose rather than fight: a single repeating fault costs
 *     exactly ONE of the twenty slots, not one per repetition.
 */
const MAX_CLIENT_ERROR_REPORTS = 20
let reportsSent = 0
const seenReportKeys = new Set<string>()

export function reportClientError(
  error: unknown,
  context: { route: string; kind: 'boundary' | 'window' | 'rejection' },
): void {
  const message = error instanceof Error ? error.message : String(error)
  const stack = error instanceof Error ? error.stack : undefined

  // `kind` first in the key: the SAME message can legitimately arrive via
  // two different paths (a caught render throw AND, separately, an
  // unhandled rejection with the identical wording) and those are worth
  // seeing as two distinct facts, not folded into one.
  const dedupeKey = `${context.kind}:${message}`
  if (seenReportKeys.has(dedupeKey)) return
  if (reportsSent >= MAX_CLIENT_ERROR_REPORTS) return
  seenReportKeys.add(dedupeKey)
  reportsSent += 1

  // `sendErrorBeacon` (`api/client.ts`) carries the transport — `keepalive`,
  // the POST, and the swallowed failure — and nothing else. The two limits
  // above and the four `.slice` caps in the object below stay HERE: they are
  // this reporter's own policy about what is worth sending, not a property
  // of the route, and the client function must not impose them on a future
  // caller that has different ones.
  sendErrorBeacon({
    clientBuild: CLIENT_BUILD.slice(0, 64),
    route: context.route.slice(0, 256),
    kind: context.kind,
    message: message.slice(0, 2_000),
    stack: stack?.slice(0, 8_000),
  })
}

/**
 * Test-only reset for this module's two pieces of module-scope state —
 * following the same plain, pure-export shape this codebase already uses
 * throughout (`resetRegistryForTests` and its seven siblings in `core`/
 * `contracts`; `pwa/useUpdatePrompt.ts`'s own `resetUpdatePromptForTests`,
 * added in this same fix wave). Genuinely plain, unlike `pwa/skew.ts`'s
 * `resetSkewAndSubscribersForTests` — `seenReportKeys` is a dedupe set, not
 * a listener set, so nothing outside this module is watching it. Without
 * this, one test's reports would count toward — and
 * potentially exhaust the dedupe set or the cap for — the very next test
 * in the same file.
 */
export function resetReportingForTests(): void {
  reportsSent = 0
  seenReportKeys.clear()
}

/**
 * Installed exactly once, from `main.tsx`. Catches the two classes of error
 * a React error boundary structurally CANNOT: a throw outside any component
 * render (a plain `window` error — a stray script, a timer callback, an
 * event handler with no try/catch of its own) and an unhandled Promise
 * rejection (an `await` with nobody watching). Neither passes through
 * React's reconciler, so `componentDidCatch` never sees either —
 * `ErrorBoundary.tsx`'s own doc comment states the same boundary from the
 * other side.
 *
 * `route: window.location.pathname` here, NOT `useLocation()` — this runs
 * outside any component, let alone inside the router's context, so the DOM
 * location is the only "current route" available. Same reasoning
 * `reportClientError`'s own callers inside `ErrorBoundary.tsx` follow, kept
 * consistent rather than mixing the two ways of asking "where am I".
 */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (event) => {
    reportClientError(event.error ?? event.message, {
      route: window.location.pathname,
      kind: 'window',
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason, { route: window.location.pathname, kind: 'rejection' })
  })
}
